import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PreviewRowCountButtonProps {
  /** 件数を取りに行く。検証エラーなど実行できない場合はメッセージを throw する */
  onFetch: () => Promise<number>;
  /** マスタ読み込み中などで押させない場合 */
  disabled?: boolean;
  /**
   * 抽出条件の指紋。値が変わったら表示中の件数を捨てる。
   * 条件を変えたあとに前の件数が残っていると、実際の出力と食い違って見えるため。
   */
  conditionKey: string;
}

/**
 * 実行前に出力件数を確かめるボタン（FKV3-12941）
 *
 * 0件のまま実行して空のCSVを受け取る、という事態を避けるためのもの。
 * 抽出ジョブと同じSQLを件数で包んで同期実行するので、ここに出る件数は実際の出力行数と一致する。
 */
export function PreviewRowCountButton({
  onFetch,
  disabled = false,
  conditionKey,
}: PreviewRowCountButtonProps) {
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 最新の問い合わせ番号。これと一致しない応答は捨てる（件数の取得には数秒かかるため、
  // 待っている間に条件が変わると、古い条件の件数が新しい条件のものとして表示されてしまう）
  const latestRequestRef = useRef(0);

  // 条件が変わったら前の件数は当てにならないので捨て、実行中の問い合わせも無効にする
  useEffect(() => {
    latestRequestRef.current += 1;
    setRowCount(null);
    setError(null);
    // 無効にした問い合わせの完了を待たずに、新しい条件で押せる状態へ戻す
    setLoading(false);
  }, [conditionKey]);

  const handleClick = async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const count = await onFetch();
      if (latestRequestRef.current !== requestId) return;
      setRowCount(count);
    } catch (e) {
      if (latestRequestRef.current !== requestId) return;
      setRowCount(null);
      setError(e instanceof Error ? e.message : '件数の取得に失敗しました');
    } finally {
      if (latestRequestRef.current === requestId) setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <Button type="button" variant="outline" onClick={handleClick} disabled={disabled || loading}>
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        件数を確認
      </Button>
      {error && <span className="text-sm text-destructive">{error}</span>}
      {!error && rowCount !== null && (
        <span
          className={
            rowCount === 0 ? 'text-sm font-medium text-amber-700' : 'text-sm text-muted-foreground'
          }
        >
          {rowCount === 0
            ? '0件（この条件では出力されません）'
            : `出力予定 ${rowCount.toLocaleString()} 行`}
        </span>
      )}
    </div>
  );
}
